<template>
  <div class="w-full flex flex-col gap-5">
    <template v-if="page?.pilarPage?.isPillarPage">
      <div
        class="pillar-page-content lg:max-w-[1280px] mx-auto flex flex-col gap-6 px-4"
      >
        <div
          class="w-full flex flex-col px-8 md:px-20 items-center justify-center gap-4 min-h-[100svh] -mt-[92px]"
        >
          <NuxtImg
            v-if="pillarPageData.heroImage"
            class="absolute w-[100vw] h-[100svh] object-cover z-[-1]"
            :src="pillarPageData.heroImage.sourceUrl"
            :alt="pillarPageData.heroImage.altText"
            provider="custom"
            width="1920"
            height="1280"
            format="webp"
            fit="scale-down"
          />
          <div
            class="bg-blue-dark/10 absolute w-[100vw] h-[100svh] object-cover z-[-1]"
          ></div>
          <div class="w-full flex flex-col gap-4 text-white drop-shadow">
            <h1
              class="text-2xl md:text-3xl font-bold text-center [text-wrap:balance] w-full -mb-2"
            >
              {{ pillarPageData.heroTitle }}
            </h1>
            <h2
              class="text-lg md:text-xl font-bold text-center [text-wrap:balance] w-full -mb-2"
            >
              {{ pillarPageData.heroSubtitle }}
            </h2>
          </div>
        </div>
        <SiteBreadcrumbs class="!py-0" :items="breadcrumbs" />
        <div class="w-full flex flex-col gap-4">
          <h1
            class="text-xl md:text-2xl font-bold text-center [text-wrap:balance] w-full -mb-2"
          >
            {{ page.title }}
          </h1>
          <div class="w-full" v-html="pillarPageData.initialParagraph"></div>
        </div>
        <div class="w-full flex max-md:flex-wrap gap-4">
          <div
            class="image-container md:max-w-[33.33%] w-full flex flex-col gap-4"
          >
            <NuxtImg
              v-if="pillarPageData.image1"
              class="w-full h-full object-cover rounded-lg"
              :src="pillarPageData.image1.sourceUrl"
              :alt="pillarPageData.image1.altText"
              provider="custom"
              width="800"
              height="500"
              format="webp"
              fit="scale-down"
            />
            <AppButton
              v-if="pillarPageData.cta1"
              :to="'#' + (pillarPageData.ctaAnchor1 || 'FORM')"
              class="w-full py-2 px-4 min-h-[48px] leading-5 rounded bg-primary font-bold uppercase text-regular"
            >
              {{ pillarPageData.cta1 }}
            </AppButton>
          </div>
          <div class="flex-1 flex flex-col gap-4">
            <h2 class="text-lg font-bold text-center -mb-2">
              {{ pillarPageData.subTitle1 }}
            </h2>
            <div v-html="pillarPageData.paragraph1"></div>
          </div>
        </div>
        <div
          v-if="pillarPageData.buildingsId"
          class="buildings-section w-full flex flex-col gap-4"
        >
          <h2 class="text-lg font-bold text-center -mb-2">
            {{ t('common.bestHousesForSale') }}
          </h2>
          <div
            class="w-full max-w-[calc(100vw-2rem)]"
            v-html="pillarPageData.buildingsId"
          ></div>
        </div>
        <div class="w-full flex max-md:flex-wrap gap-4">
          <div class="w-full flex flex-col gap-4">
            <h2 class="text-lg font-bold text-center -mb-2">
              {{ pillarPageData.subTitle2 }}
            </h2>
            <div v-html="pillarPageData.paragraph2"></div>
          </div>
          <div
            class="video-container w-full md:max-w-[40%]"
            v-html="pillarPageData.video2"
          ></div>
        </div>
        <div class="w-full flex flex-col gap-4">
          <h2 class="text-lg font-bold text-center -mb-2">
            {{ pillarPageData.subTitle3 }}
          </h2>
          <div v-html="pillarPageData.paragraph3"></div>
          <AppButton
            v-if="pillarPageData.cta2"
            :to="'#' + (pillarPageData.ctaAnchor1 || 'FORM')"
            class="w-full min-w-[50%] max-w-[max-content] mx-auto py-2 px-4 min-h-[48px] leading-5 rounded bg-primary font-bold uppercase text-regular"
          >
            {{ pillarPageData.cta2 }}
          </AppButton>
        </div>
        <div class="w-full flex max-md:flex-wrap gap-4">
          <div
            class="image-container md:max-w-[33.33%] w-full flex flex-col gap-4"
          >
            <NuxtImg
              v-if="pillarPageData.image4"
              class="w-full h-full object-cover rounded-lg"
              :src="pillarPageData.image4.sourceUrl"
              :alt="pillarPageData.image4.altText"
              provider="custom"
              width="800"
              height="500"
              format="webp"
              fit="scale-down"
            />
          </div>
          <div class="flex-1 flex flex-col gap-4">
            <h2 class="text-lg font-bold text-center -mb-2">
              {{ pillarPageData.subTitle4 }}
            </h2>
            <div v-html="pillarPageData.paragraph4"></div>
          </div>
        </div>
        <div class="w-full flex max-md:flex-wrap gap-4">
          <div
            v-if="pillarPageData.subTitle5 && pillarPageData.embedCode"
            :id="pillarPageData.ctaAnchor1 || 'FORM'"
            class="md:max-w-[33.33%] w-full flex flex-col pt-24 -mt-24"
          >
            <h2 class="text-lg font-bold text-center mb-2">
              {{ pillarPageData.subTitle5 }}
            </h2>
            <div v-html="pillarPageData.embedCode"></div>
          </div>
          <div class="flex-1 flex flex-col gap-4">
            <h2 class="text-lg font-bold text-center -mb-2">
              {{ pillarPageData.faqTitle }}
            </h2>
            <details
              v-for="(faq, index) in pillarPageData.faqQuestions"
              :key="index"
              class="accordion-item"
            >
              <summary
                class="flex items-center rounded border border-neutral px-4 py-2"
              >
                <h3 class="font-bold">
                  {{ faq.faqQuestionTitle }}
                </h3>
                <Icon class="close-icon ml-auto" :path="mdiMinus" />
                <Icon class="open-icon ml-auto" :path="mdiPlus" />
              </summary>
              <div class="content px-4 pt-2">
                <div v-html="faq.faqAnwser"></div>
              </div>
            </details>
          </div>
        </div>
        <h2 class="text-lg font-bold w-full -mb-8">
          {{ t('common.mightInterestYou') }}
        </h2>
        <SiteBlogResults
          v-if="relatedPosts?.length"
          :items="relatedPosts"
          :total-pages="0"
        />
      </div>
    </template>
    <template v-else-if="page">
      <div v-if="!fullWidth" class="page-header flex flex-col gap-5">
        <NuxtImg
          v-if="page.image"
          class="w-full max-md:max-h-[60vw] h-[250px] md:h-[325px] lg:h-[500px] object-cover rounded-lg"
          :src="page.image"
          :alt="page.imageAlt"
          provider="custom"
          width="800"
          height="500"
          format="webp"
          fit="scale-down"
        />
        <h1 class="text-xl md:text-2xl font-bold [text-wrap:balance]">
          {{ page.title }}
        </h1>
        <div class="flex gap-2 items-center mb-2 max-md:flex-col">
          <Icon class="w-6 min-w-6 h-6 text-neutral" :path="mdiCalendar" />
          <div class="text-sm text-neutral-light mr-2">
            {{ formatDate(page.date) }}
          </div>
        </div>
        <div class="title-divider mb-4"></div>
      </div>
      <div class="html-content page-content" v-html="pageContent"></div>
    </template>
    <div
      v-for="carousel in carousels"
      :key="carousel.elementId"
      :data-teleport="carousel.elementId"
      style="display: none"
    >
      <HomePropertyCardsSlider
        :id="carousel.elementId + '-slider'"
        :ids="carousel.ids"
        :limit="carousel.limit"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiCalendar, mdiMinus, mdiPlus } from '@mdi/js';
import type { PropType } from 'vue';

import type { Page } from '@/types/common';

import '@/assets/css/content.scss';

const HomePropertyCardsSlider = defineAsyncComponent(
  () => import('@/async-components/Home/PropertyCardsSlider.vue')
);
const SiteBlogResults = defineAsyncComponent(
  () => import('@/async-components/Site/Blog/Results.vue')
);
const SiteBreadcrumbs = defineAsyncComponent(
  () => import('@/async-components/Site/Breadcrumbs.vue')
);

const props = defineProps({
  page: {
    type: Object as PropType<Page | null>
  },
  fullWidth: {
    type: Boolean
  },
  breadcrumbs: {
    type: Array as PropType<
      { name: string; item: string; interactuable?: boolean }[]
    >
  }
});

const { locale, t } = useI18n();

const { language } = useLanguage();

const localePath = useLocalePath();

const carousels = ref<{ ids: string[]; limit: number; elementId: string }[]>(
  []
);

function updateTeleports() {
  const elements = document.querySelectorAll('[data-teleport]');

  elements.forEach(element => {
    const target = document.getElementById(
      element.getAttribute('data-teleport')
    );

    if (target) {
      target.appendChild(element);
      element.style.display = 'block';
      element.removeAttribute('data-teleport');
    }
  });
}

function updateCarousels() {
  const carouselsElements = document.querySelectorAll(
    '.content-carousel-container'
  );

  carousels.value = Array.from(carouselsElements).map((carousel, index) => {
    const idsString = carousel.getAttribute('data-ids') || '';

    if (!idsString) {
      console.error('Carousel ids not found');
      return {
        ids: [],
        limit: 0,
        elementId: ''
      };
    }

    const ids = idsString.split(',').map(id => id.trim());

    const limit = +(carousel.getAttribute('data-limit') || 0);

    return {
      ids,
      limit,
      elementId: `content-carousel-${index}-${ids[0]}`
    };
  });

  carouselsElements.forEach((carousel, index) => {
    carousel.id = carousels.value[index].elementId;
  });

  setTimeout(updateTeleports, 500);
}

function formatDate(date: Date) {
  return new Intl.DateTimeFormat(language.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
}

function replaceShortcodes(content: string) {
  const regex = /\[(building)\s(.*?)\]/g;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const properties: Record<string, string> = {};
    const propsRegex = /(\w+)='(.*?)'/g;
    let propsMatch;

    const type = match[1];

    const propertiesString = match[2]
      .replaceAll('"', `'`)
      .replaceAll('&#8217;', `'`)
      .replaceAll('&#8242;', `'`);

    while ((propsMatch = propsRegex.exec(propertiesString)) !== null) {
      properties[propsMatch[1]] = propsMatch[2];
    }

    const replacement = `<div class="content-carousel-container" data-type="${type}-carousel" ${Object.entries(
      properties
    )
      .map(entry => `data-${entry[0]}="${entry[1]}"`)
      .join(' ')}></div>`;
    console.log({
      match0: match[0],
      propertiesString,
      replacement,
      properties
    });

    content = content.replace(match[0], replacement);
  }

  return content;
}

const pillarPageData = computed(() => {
  if (
    !props.page?.pilarPage?.isPillarPage ||
    !props.page?.pilarPage?.pillarPageData
  ) {
    return {} as Partial<typeof props.page.pilarPage.pillarPageData>;
  }

  return Object.fromEntries(
    Object.entries(props.page.pilarPage.pillarPageData).map(entry => {
      // if (entry[0] === 'paragraph3') {
      //   entry[1] =
      //     entry[1] + `[building ids='A11565746, A11563888' limit='15']`;
      // }
      return [
        entry[0],
        typeof entry[1] === 'string' ? replaceShortcodes(entry[1]) : entry[1]
      ];
    })
  ) as typeof props.page.pilarPage.pillarPageData;
});

const relatedPosts = computed(() => {
  if (
    !props.page?.pilarPage?.isPillarPage ||
    !props.page?.pilarPage?.pillarPageData?.relatedPost?.length ||
    !pillarPageData.value.relatedPost
  ) {
    return [];
  }

  return pillarPageData.value.relatedPost.map(node => {
    const to = localePath(
      `/${t('routes.blog')}` +
        `/${node.categories.nodes[0]?.slug}` +
        `/${node.slug}`
    );

    const category = node.categories.nodes[0];

    return {
      title: node.title,
      excerpt: node.excerpt,
      to,
      image: node.featuredImage?.node?.sourceUrl,
      imageAlt: node.featuredImage?.node?.altText,
      category: category?.name,
      categoryLink: localePath(`/${t('routes.blog')}` + `/${category?.slug}`)
    };
  });
});

const pageContent = computed(() => {
  return replaceShortcodes(props.page.content);
});

onMounted(() => {
  nextTick(() => {
    updateCarousels();
  });
});
</script>

<style scoped lang="scss">
.comma-separeted-list {
  li {
    display: inline-block;
    &:not(:last-child) {
      &::after {
        content: ',';
        margin-right: 0.5em;
      }
    }
  }
}

.accordion-item {
  summary {
    list-style: none;
    cursor: pointer;
    user-select: none;
  }

  .close-icon {
    display: none;
  }

  &[open] {
    .close-icon {
      display: block;
    }
    .open-icon {
      display: none;
    }
  }
}

.video-container {
  @apply flex items-center justify-center;
  :deep(iframe) {
    @apply w-[inherit] h-[inherit] aspect-[16/9];
  }
}

:deep(.page-content) {
  .elementor > .elementor-element {
    @apply mb-4 lg:mb-8;

    & > * {
      @apply px-4 max-lg:max-w-[768px] lg:max-w-[1280px] mx-auto;
    }

    h1,
    h2,
    h3 {
      @apply max-w-[840px];
    }

    &.elementor-pfs-2-col {
      @apply bg-blue-lighter mb-8;

      .e-con-inner {
        @apply flex max-md:flex-col items-stretch justify-stretch gap-x-4 lg:gap-12;

        & > * {
          @apply w-full min-w-[40%] md:max-w-[60%];
          &:not(:has(img)) {
            @apply py-4 lg:py-8 self-center;
          }
          &:has(img) {
            @apply max-md:order-first max-md:pt-4;
            @apply w-full md:max-w-[40%];
            & * {
              @apply h-full max-md:max-h-[240px];
            }
          }
          img {
            @apply rounded-none #{!important};
          }
        }
      }

      h1,
      h2,
      h3 {
        @apply mb-3;
      }

      img {
        @apply object-cover;
      }

      .elementor-widget-button {
        @apply mt-4;
      }
    }
  }

  .elementor-pfs-cards-container > * {
    @apply flex items-stretch justify-center gap-4 mb-8;
  }

  .elementor-pfs-icons-container {
    .e-con-inner {
      @apply grid lg:grid-cols-3 gap-x-12 max-w-[960px] mx-auto;
      .elementor-icon-box-wrapper {
        @apply flex flex-col items-center text-center gap-2;
      }
    }
    .e-font-icon-svg {
      @apply h-12 w-12 max-w-[3rem] fill-blue-light;
    }
    h3 {
      @apply text-lg mb-2 #{!important};
    }
  }

  .elementor-pfs-faq-container {
    @apply mt-2;

    details > summary {
      list-style: none;
    }

    details > summary::-webkit-details-marker {
      display: none;
    }

    details > div {
      @apply px-4 pt-2 text-sm;
    }

    .e-n-accordion {
      @apply flex flex-col gap-4;
    }

    .e-n-accordion-item-title {
      @apply flex items-center bg-blue-lighter py-1 px-4 cursor-pointer;
    }

    .e-n-accordion-item-title-icon {
      @apply ml-auto w-4 h-4 max-w-[1rem];
    }

    .e-opened {
      @apply hidden;
    }

    .e-n-accordion-item[open] {
      .e-closed {
        @apply hidden;
      }
      .e-opened {
        @apply block;
      }
    }
  }

  .elementor-pfs-card,
  .elementor-pfs-card ~ * {
    // TODO: Remove ~
    @apply h-auto border-transparent shadow-lg #{!important};
    @apply rounded-lg overflow-hidden max-w-[360px];
    .elementor-widget-image img {
      @apply aspect-[3/2] object-cover;
    }
    & > *:not(.elementor-widget-image) {
      @apply p-4 text-sm;
      h3 {
        @apply text-center mb-3 text-lg leading-5 [text-wrap:balance] #{!important};
      }
    }
    .elementor-button-wrapper {
      @apply w-full text-center mt-1 mb-6;
    }
  }

  .hs-button,
  .elementor-widget-button input,
  .elementor-widget-button a,
  .elementor-widget-button button {
    @apply bg-primary text-white uppercase #{!important};
    @apply inline-flex font-bold h-[40px] px-4 rounded shadow items-center;
    @apply cursor-pointer hover:bg-primary-light focus-visible:bg-primary-light hover:bg-primary-light active:bg-primary-lighter;
  }

  .hbspt-form {
    @apply max-w-[500px] mx-auto;
    fieldset {
      @apply flex-row #{!important};
    }
  }
  .elementor-hidden-desktop,
  .elementor-hidden-tablet,
  .elementor-shape-bottom {
    display: none;
  }
  .elementor-align-center {
    @apply text-center;
  }

  .twae-timeline {
    @apply flex flex-col gap-4 md:gap-1 mx-auto md:max-w-[960px] md:-mt-4 mb-4;
    .twae-story {
      @apply relative;

      .twae-icon {
        @apply absolute md:left-1/2 md:-translate-x-1/2 top-1/2 -translate-y-1/2;
      }

      .twae-content {
        @apply md:w-[calc(50%-64px)] w-[calc(100%-96px)] p-4 rounded shadow bg-white max-md:ml-auto;
      }

      .twae-title {
        @apply font-bold;
      }

      &.twae-story-right .twae-content {
        @apply ml-auto;
      }
    }
  }
}
</style>

<style lang="scss">
.pillar-page-content {
  h3,
  h4,
  h5,
  h6 {
    @apply font-bold mt-4 mb-1;
  }
}
</style>
